import type { InvestorEncompassTemplate } from '@/types/investor-encompass';

// HMDA Type of Purchaser codes (Reg C / 2018 HMDA). Numeric object keys are coerced to
// string keys by JS, so lookups are by string code throughout.
export const HMDA_PURCHASER_TYPES: Record<string, string> = {
  0: 'Not applicable',
  1: 'Fannie Mae',
  2: 'Ginnie Mae',
  3: 'Freddie Mac',
  4: 'Farmer Mac',
  5: 'Private securitizer',
  6: 'Commercial bank, savings bank, or savings association',
  71: 'Credit union, mortgage company, or finance company',
  72: 'Life insurance company, credit union, mortgage bank, or finance company',
  8: 'Affiliate institution',
  9: 'Other type of purchaser'
};

/** Lowercase alphanumerics only — for fuzzy enum-name / description matching. */
const normalizeKey = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Encompass delivers the purchaser type as an enum NAME (not a numeric HMDA code) —
 * e.g. "CreditUnionMortgageOrFinanceComapny" (note the live misspelling). Map the
 * known enum names → code. Keys are normalized via normalizeKey.
 */
const ENCOMPASS_PURCHASER_ENUM_TO_CODE: Record<string, string> = {
  notapplicable: '0',
  fanniemae: '1',
  ginniemae: '2',
  freddiemac: '3',
  farmermac: '4',
  privatesecuritizer: '5',
  privatesecuritization: '5',
  commercialbanksavingsbankorsavingsassociation: '6',
  creditunionmortgagecompanyorfinancecompany: '71',
  creditunionmortgageorfinancecompany: '71',
  creditunionmortgageorfinancecomapny: '71', // live (misspelled) value in prod data
  lifeinsurancecompanycreditunionmortgagebankorfinancecompany: '72',
  affiliateinstitution: '8',
  othertypeofpurchaser: '9',
  other: '9'
};

/** Reverse map of canonical descriptions → code (normalized) so purchaser_type_value resolves. */
const DESCRIPTION_TO_CODE: Record<string, string> = Object.entries(HMDA_PURCHASER_TYPES).reduce(
  (acc, [code, desc]) => {
    acc[normalizeKey(desc)] = code;
    return acc;
  },
  {} as Record<string, string>
);

/** Resolve a single raw value to an HMDA code string, or null if it can't be resolved. */
const resolveCode = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const raw = String(value).trim();
  if (raw === '') {
    return null;
  }
  // 1) Pure numeric code — tolerate string or number ("71"/71/"0"). 0 is valid.
  if (/^\d+$/.test(raw)) {
    return raw;
  }
  const norm = normalizeKey(raw);
  // 2) Encompass enum name.
  if (ENCOMPASS_PURCHASER_ENUM_TO_CODE[norm]) {
    return ENCOMPASS_PURCHASER_ENUM_TO_CODE[norm];
  }
  // 3) Canonical description text.
  if (DESCRIPTION_TO_CODE[norm]) {
    return DESCRIPTION_TO_CODE[norm];
  }
  return null;
};

/** The authoritative numeric HMDA code for a template, or null when none can be resolved. */
export function getHmdaPurchaserCode(template: InvestorEncompassTemplate): string | null {
  return resolveCode(template.purchaserType)
    ?? resolveCode(template.rawTemplateData?.purchaserType)
    ?? resolveCode(template.rawTemplateData?.purchaser_type)
    ?? resolveCode(template.purchaserTypeValue);
}

/**
 * Display string for the HMDA purchaser type, or null when nothing is set
 * (the caller renders an italic muted "Not set" for null — not this helper).
 * Resolved → "71. Credit union, …"; code 0 → "0. Not applicable" (0 is valid, not
 * falsy); a present-but-unmappable value → "<raw>. (Unknown)" + a console warning.
 */
export function formatHmdaPurchaserType(template: InvestorEncompassTemplate): string | null {
  const code = getHmdaPurchaserCode(template);
  if (code === null) {
    const raw = template.purchaserType ?? template.purchaserTypeValue;
    if (raw === null || raw === undefined || String(raw).trim() === '') {
      return null;
    }
    console.warn(`Unknown HMDA purchaser type: ${String(raw)}`);
    return `${String(raw)}. (Unknown)`;
  }
  const desc = HMDA_PURCHASER_TYPES[code];
  if (!desc) {
    console.warn(`Unknown HMDA purchaser type code: ${code}`);
    return `${code}. (Unknown)`;
  }
  return `${code}. ${desc}`;
}
